import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { galleryItems as _seedGallery, locations, testimonials, slugify } from '../mockData';
import { useGallery } from '../lib/usePublicData';
import { PaintingFrame } from '../components/PaintingFrame';
import NewsletterSignup from '../components/NewsletterSignup';
import InstagramFeed from '../components/InstagramFeed';
import PromoVideo from '../components/PromoVideo';
import { useT, localize } from '../lib/i18n';

const Home = () => {
  const { t, lang } = useT();
  const { items: galleryItems } = useGallery();
  const featuredItems = galleryItems.filter(item => item.featured).slice(0, 3);

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-ivory">
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-15">
            <img
              src="https://images.pexels.com/photos/27926537/pexels-photo-27926537.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Art Deco Interior"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-cream/80 via-ivory/70 to-cream/90"></div>
        </div>

        {/* Art Deco Geometric Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="h-full w-full deco-pattern"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <div className="inline-block mb-12">
            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="h-px w-24 bg-gold"></div>
              <div className="w-3 h-3 rotate-45 bg-dark-crimson border-2 border-gold"></div>
              <div className="h-px w-24 bg-gold"></div>
            </div>
          </div>
                    <h1 className="text-7xl md:text-8xl lg:text-9xl font-bold mb-4 text-dark-green" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
            THE CHIMERA
          </h1>
          <h2 className="text-5xl md:text-6xl font-light mb-8 text-dark-crimson" style={{ fontFamily: 'Playfair Display, serif', fontWeight: 300, letterSpacing: '0.15em' }}>
            Gallery
          </h2>
          <div className="h-1 w-32 bg-gold mx-auto mb-10"></div>
                    <p className="text-2xl md:text-3xl text-dark-brown mb-4 leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            {t('home.heroTagline')}
          </p>
          <p className="text-xl text-rich-green mb-16" style={{ fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.2em' }}>
            {t('home.heroLocations')}
          </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/gallery">
              <Button className="bg-dark-crimson hover:bg-crimson text-cream px-12 py-7 text-lg rounded-none border-2 border-gold transition-all duration-300 hover:scale-105 hover:shadow-xl font-semibold tracking-wider">
                {t('home.exploreGallery')}
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
            <Link to="/annex">
              <Button variant="outline" className="bg-transparent border-2 border-rich-green text-dark-green hover:bg-rich-green hover:text-cream px-12 py-7 text-lg rounded-none transition-all duration-300 hover:scale-105 font-semibold tracking-wider">
                {t('nav.annex')}
              </Button>
            </Link>
            <PromoVideo />
          </div>

          <div className="mt-20 flex items-center justify-center gap-6">
            <div className="h-px w-24 bg-gold"></div>
            <div className="w-3 h-3 rotate-45 bg-dark-crimson border-2 border-gold"></div>
            <div className="h-px w-24 bg-gold"></div>
          </div>
        </div>
      </section>

      {/* Featured Collection */}
      <section className="py-24 px-6 bg-cream deco-pattern">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-8">
              <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
              <h2 className="text-6xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
                {t('home.featured')}
              </h2>
              <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6"></div>
            </div>
            <p className="text-charcoal text-xl max-w-2xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('home.featuredSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {featuredItems.map((item) => (
              <Card key={item.id} className="bg-ivory border-2 border-gold/30 overflow-hidden group hover:border-dark-crimson transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                <div className="relative h-80 overflow-hidden bg-ivory flex items-center justify-center px-10 py-8">
                  <img
                    src={item.image}
                    alt={item.title}
                    className={`max-w-full max-h-full object-contain transition-opacity duration-700 ${item.imageContext ? 'group-hover:opacity-0' : ''}`}
                  />
                  {item.imageContext && (
                    <img
                      src={item.imageContext}
                      alt={`${item.title} in gallery`}
                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    />
                  )}
                </div>
                <CardContent className="p-8 bg-ivory">
                  <h3 className="text-2xl font-bold text-dark-green mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {localize(item, 'title', lang)}
                  </h3>
                  <Link
                    to={`/artists/${slugify(item.artist)}`}
                    className="text-dark-crimson mb-1 font-semibold inline-block hover:underline hover:text-crimson transition-colors"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {item.artist}
                  </Link>
                  <p className="text-charcoal/70 text-sm mb-4">{item.year}</p>
                  <p className="text-charcoal mb-6 leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{localize(item, 'description', lang)}</p>
                  <div className="flex justify-between items-center border-t-2 border-gold/30 pt-6">
                    <span className="text-3xl font-bold text-gold">{item.price}</span>
                    <Button variant="ghost" className="text-dark-green hover:text-dark-crimson hover:bg-gold/10 font-semibold">
                      {t('home.viewDetails')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/gallery">
              <Button className="bg-transparent border-2 border-gold text-dark-green hover:bg-gold hover:text-dark-brown px-12 py-6 text-lg rounded-none transition-all duration-300 font-semibold tracking-wider">
                {t('home.viewFullGallery')}
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter signup */}
      <NewsletterSignup />

      {/* Instagram */}
      <InstagramFeed />

      {/* Locations */}
      <section className="py-24 px-6 bg-ivory">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
            <h2 className="text-6xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
              {t('home.locations')}
            </h2>
            <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {locations.map((location, index) => (
              <Card key={index} className="bg-cream border-2 border-gold/50 p-10 hover:border-dark-crimson transition-all duration-300 group hover:shadow-xl">
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-14 h-14 bg-dark-crimson flex items-center justify-center group-hover:bg-gold transition-colors duration-300 border-2 border-gold">
                    <MapPin className="text-cream" size={26} />
                  </div>
                  <div>
                    <h3 className="text-4xl font-bold text-dark-green mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {location.city}
                    </h3>
                    <p className="text-dark-crimson text-2xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{location.country}</p>
                  </div>
                </div>
                <div className="space-y-4 text-charcoal" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>
                  <p>{location.address}</p>
                  <p className="text-gold font-semibold text-lg">{location.phone}</p>
                  <p className="text-sm text-charcoal/70">{location.hours}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 bg-cream deco-pattern">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
            <h2 className="text-6xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
              {t('home.testimonials')}
            </h2>
            <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-ivory border-2 border-gold/30 p-10 hover:border-dark-crimson transition-all duration-300 hover:shadow-xl">
                <div className="flex gap-1 mb-8">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-gold fill-gold" size={22} />
                  ))}
                </div>
                <p className="text-charcoal mb-8 italic text-lg leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  "{testimonial.text}"
                </p>
                <div className="border-t-2 border-gold/30 pt-6">
                  <p className="text-dark-green font-bold text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>{testimonial.name}</p>
                  <p className="text-dark-crimson text-sm font-semibold mt-1">{testimonial.role}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
